import { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
import type {
  ConnectionStatus,
  ConnectionConfig,
  UseConnectionStatusResult,
} from "@pineseed/gui/hook/useConnectionStatus";

export type ConnectionContextValue = UseConnectionStatusResult;

const ConnectionContext = createContext<ConnectionContextValue | null>(null);

const initialStatus: ConnectionStatus = {
  connected: false,
  retryCount: 0,
  lastError: null,
  lastAttempt: null,
  nextRetryAt: null,
  isRetrying: false,
};

export function ConnectionProvider({
  config,
  children,
}: {
  config: ConnectionConfig | undefined;
  children: React.ReactNode;
}) {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(initialStatus);
  const retryCountRef = useRef(0);

  useEffect(() => {
    const updateStatus = (updates: Partial<ConnectionStatus>) => {
      setConnectionStatus((prev) => ({ ...prev, ...updates }));
    };

    if (!config) {
      updateStatus({ connected: false, isRetrying: false, retryCount: 0, lastError: null });
      return;
    }

    retryCountRef.current = 0;
    let isActive = true;
    let wsConnected = false;
    let httpConnected = false;

    const updateStatusGuarded = (updates: Partial<ConnectionStatus>) => {
      if (isActive) updateStatus(updates);
    };

    const checkOverall = () => {
      const hasWs = !!config.websocket;
      const hasHttp = !!config.http;
      const connected =
        hasWs && hasHttp ? wsConnected && httpConnected :
        hasWs ? wsConnected :
        hasHttp ? httpConnected : false;
      updateStatusGuarded({ connected });
    };

    let reconnectTimeout: number | null = null;
    let httpInterval: number | null = null;
    let ws: WebSocket | null = null;

    const connectWebSocket = () => {
      if (!config.websocket || !isActive) return;

      ws = new WebSocket(config.websocket.url);

      ws.onopen = () => {
        wsConnected = true;
        retryCountRef.current = 0;
        updateStatusGuarded({ isRetrying: false, lastError: null, retryCount: 0 });
        checkOverall();
      };

      ws.onclose = () => {
        if (!isActive) return;
        wsConnected = false;
        checkOverall();

        const { maxReconnectAttempts, reconnectInterval, maxReconnectInterval } = config.websocket!;
        const attempt = retryCountRef.current;

        if (attempt < maxReconnectAttempts) {
          const exponential = Math.min(reconnectInterval * 2 ** attempt, maxReconnectInterval);
          const jittered = Math.random() * exponential;
          retryCountRef.current = attempt + 1;
          updateStatusGuarded({ isRetrying: true, retryCount: retryCountRef.current });
          reconnectTimeout = window.setTimeout(connectWebSocket, jittered);
        }
      };

      ws.onerror = () => {
        wsConnected = false;
        updateStatusGuarded({ lastError: new Error("WebSocket connection failed"), isRetrying: false });
        checkOverall();
      };
    };

    const startHttpPolling = () => {
      if (!config.http || !isActive) return;

      const { url, timeout, interval } = config.http;

      const poll = async () => {
        try {
          const controller = new AbortController();
          const t = window.setTimeout(() => controller.abort(), timeout);
          const response = await fetch(url, { signal: controller.signal, method: "HEAD" });
          window.clearTimeout(t);
          httpConnected = response.ok;
          updateStatusGuarded({ isRetrying: false, lastError: null });
          checkOverall();
        } catch (error) {
          httpConnected = false;
          updateStatusGuarded({ lastError: error instanceof Error ? error : new Error("HTTP poll failed") });
          checkOverall();
        }
      };

      poll();
      httpInterval = window.setInterval(poll, interval);
    };

    if (config.websocket) connectWebSocket();
    if (config.http) startHttpPolling();

    return () => {
      isActive = false;
      ws?.close();
      if (reconnectTimeout !== null) window.clearTimeout(reconnectTimeout);
      if (httpInterval !== null) window.clearInterval(httpInterval);
    };
  }, [config]);

  const value: ConnectionContextValue = useMemo(
    () => ({
      connectionStatus,
      isConnected: connectionStatus.connected,
      isRetrying: connectionStatus.isRetrying,
      retryCount: connectionStatus.retryCount,
      lastError: connectionStatus.lastError,
    }),
    [connectionStatus],
  );

  return <ConnectionContext value={value}>{children}</ConnectionContext>;
}

export function useConnectionContext(): ConnectionContextValue {
  const ctx = useContext(ConnectionContext);
  if (!ctx) throw new Error("useConnectionContext must be used within a ConnectionProvider");
  return ctx;
}
